import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from '@/react-query/api';
import { endpoints } from '@/react-query/endpoints';

export interface CreateStandalonePatientVisitRequest {
  firstName: string;
  lastName?: string;
  sourcePatientId?: string;
  dateOfBirth?: string;
  appointmentDate: string;
  appointmentStartTime: string;
  appointmentOn?: string;
}

export interface CreateStandalonePatientVisitResponse {
  id: string;
  firstName: string;
  lastName: string | null;
  sourcePatientId: string | null;
  dateOfBirth: Date | null;
  appointmentDate: Date;
  appointmentStartTime: string;
  appointmentOn: Date | null;
  practiceId: number;
  createdAt: Date;
  updatedAt: Date;
}

export const useCreateStandalonePatientVisit = () => {
  const queryClient = useQueryClient();
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useMutation<
    CreateStandalonePatientVisitResponse,
    unknown,
    CreateStandalonePatientVisitRequest
  >({
    mutationFn: async (data: CreateStandalonePatientVisitRequest) => {
      const response =
        await makeAPIRequestReactQuery<CreateStandalonePatientVisitResponse>(
          endpoints.ambient.standalonePatientVisits,
          undefined, // No params for POST
          'POST',
          {
            data, // Send data in request body
            headers: {
              practice: currentPracticeId,
              org: currentOrganizationId,
              'Content-Type': 'application/json',
            },
          },
        );

      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch the standalone patient visits list
      queryClient.invalidateQueries([
        endpoints.ambient.standalonePatientVisits,
        currentPracticeId,
      ]);
      queryClient.invalidateQueries([
        endpoints.ambient.voiceNotes.combinedAppointmentsList,
      ]);
    },
  });
};
